import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  NavigationError,
  provideRouter,
  TitleStrategy,
  withNavigationErrorHandler,
  withRouterConfig,
  withViewTransitions
} from '@angular/router';
import { AppComponent } from '@app/app.component';
import { ROUTES } from '@app/app.routes';
import { JwtModule } from '@auth0/angular-jwt';
import { ErrorHandlerModule } from '@common/components/error-handler/error-handler.module';
import { errorHandlerInterceptor } from '@common/interceptors/error-handler.interceptor';
import { sentryInterceptor } from '@common/interceptors/sentry.interceptor';
import { sessionInterceptor } from '@common/interceptors/session.interceptor';
import { SortOrderByPipe } from '@common/pipes';
import { AppTitleStrategy, ThemeService } from '@common/services';
import { AppInitService } from '@common/services/app-init.service';
import { GlobalErrorHandler } from '@common/services/global-error-handler.service';
import { tokenGetter } from '@common/utils';
import { environment } from '@env/environment';
import { NgbDateAdapter, NgbDateNativeAdapter, NgbTimeAdapter } from '@ng-bootstrap/ng-bootstrap';
import * as Sentry from '@sentry/angular';
import { I18_NAMESPACE_APPS_UI, I18_NAMESPACES_APPS_UI, I18_NAMESPACES_MBS_UI_KIT, I18N_PROVIDERS } from 'i18n';
import { ModalService, SHARED_PERSISTENT_STATE_PROVIDERS } from 'mbs-ui-kit';
import { NgbTimeStringAdapterFormat12 } from 'mbs-ui-kit/form/timepicker/ngb-time-adapters/ngb-time-string-adapter-format-12';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { loadConfigurations, MBS_APP_CONFIG } from './config.loader';

const modules = [
  ErrorHandlerModule.forRoot(),
  IconSpriteModule.forRoot({ path: './assets/svg/sprites/sprite.svg' }),
  JwtModule.forRoot({ config: { tokenGetter } })
];

function mountApp(config: string) {
  bootstrapApplication(AppComponent, {
    providers: [
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(
        ROUTES,
        withNavigationErrorHandler((error) => chunkLoadError(error)),
        withRouterConfig({ paramsInheritanceStrategy: 'always', resolveNavigationPromiseOnError: true }),
        withViewTransitions({ skipInitialTransition: true })
      ),
      provideHttpClient(
        withXhr(),
        withInterceptors([errorHandlerInterceptor, sentryInterceptor, sessionInterceptor]),
        withInterceptorsFromDi()
      ),
      importProvidersFrom(...modules),
      provideAppInitializer(() => void inject(Sentry.TraceService)),
      provideAppInitializer(() => void inject(AppInitService).validate()),
      provideAppInitializer(() => void inject(ThemeService).init()),
      { provide: MBS_APP_CONFIG, useValue: config },
      { provide: TitleStrategy, useClass: AppTitleStrategy },
      { provide: NgbTimeAdapter, useClass: NgbTimeStringAdapterFormat12 },
      { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
      { provide: ErrorHandler, useClass: GlobalErrorHandler },
      I18N_PROVIDERS(environment.localePath, [...I18_NAMESPACES_APPS_UI, ...I18_NAMESPACES_MBS_UI_KIT], I18_NAMESPACE_APPS_UI.app),
      SHARED_PERSISTENT_STATE_PROVIDERS,
      DatePipe,
      SortOrderByPipe,
      ModalService
    ]
  }).catch((err) => console.error(err));
}

function chunkLoadError(res: NavigationError): void {
  const { error } = res;
  const isChunkError =
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Loading chunk');

  if (!isChunkError) {
    return;
  }

  if (!sessionStorage.getItem('chunk-reload')) {
    sessionStorage.setItem('chunk-reload', 'true');
    window.location.reload();
  }
}

loadConfigurations('assets/config/config.json')
  .then((config) => mountApp(config))
  .catch((err) => console.error(err.message));
